// src/features/notes/NotesList.js
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useGetNotesQuery, makeNoteSelectors } from "./notesApiSlice";
import NoteTable from "./components/NoteTable";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100, 200];

export default function NotesList() {
    // Local filters
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("");
    const [sortBy, setSortBy] = useState("createdAt");
    const [order, setOrder] = useState("desc");
    const [page, setPage] = useState(1);     // 1-based
    const [limit, setLimit] = useState(10);

    // Build stable params
    const params = useMemo(() => {
        const p = { sortBy, order };
        if (q.trim()) p.q = q.trim();
        if (status) p.status = status;
        return p;
    }, [q, status, sortBy, order]);

    // Merge with pagination; memoize for arg-stable cache keys
    const queryArgs = useMemo(() => ({ ...(params || {}), page, limit }), [params, page, limit]);

    // Fetch paged notes (normalized in transformResponse)
    const {
        data,
        refetch,
        isLoading,
        isFetching,
        isError,
        error,
    } = useGetNotesQuery(queryArgs, {
        pollingInterval: 15000,
        refetchOnFocus: true,
        refetchOnMountOrArgChange: true,
    });

    // Arg-aware selectors for THIS page/limit (+filters)
    const { selectAllNotes, selectTotal } = useMemo(
        () => makeNoteSelectors(queryArgs),
        [queryArgs]
    );

    // Normalized list + total from the store
    const notes = useSelector(selectAllNotes);
    const total = useSelector(selectTotal);

    // Paging math
    const totalPages = Math.max(1, Math.ceil((total || 0) / limit));
    const from = total === 0 ? 0 : (page - 1) * limit + 1;
    const to = Math.min(page * limit, total || 0);

    const pageIsFull = (data?.ids?.length || 0) >= limit; // or notes.length if you prefer
    const canPrev = page > 1;
    const canNext = totalPages > 1 ? page < totalPages : pageIsFull;

    const onPrev = () => { if (canPrev) setPage(p => p - 1); };
    const onNext = () => { if (canNext) setPage(p => p + 1); };
    const onPageSizeChange = (e) => {
        const newLimit = Number(e.target.value);
        setLimit(newLimit);
        setPage(1); // reset when size changes
    };

    // Debug pagining issues, uncomment to use
    // console.log('page', page, 'limit', limit, 'ids', data?.ids?.length, 'notes', notes.length, 'total', total);
    // console.log('page', page, 'limit', limit, {
    //     hookIds: data?.ids,
    //     hookLen: data?.ids?.length,
    //     selLen: notes.length,
    //     total,
    // });
 

  const loading = isLoading || isFetching;

  return (
    <section className="p-6 space-y-6">
      {/* Header */}
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">Notes</h1>
          <p className="text-sm text-gray-500">Manage project notes and timelines.</p>
        </div>
        <Link
          to="/dash/notes/new"
          className="rounded-xl bg-black px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800"
        >
          New Note
        </Link>
      </header>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200 bg-white p-4">
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(1); }}
          placeholder="Search title/description…"
          className="w-64 rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
        />
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          className="rounded-xl border border-gray-200 px-3 py-2 text-sm"
        >
          <option value="">All status</option>
          <option value="open">Open</option>
          <option value="closed">Closed</option>
        </select>

        <div className="ml-auto flex items-center gap-2">
          <select
            value={sortBy}
            onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
            className="rounded-xl border border-gray-200 px-3 py-2 text-sm"
          >
            <option value="createdAt">Created</option>
            <option value="title">Title</option>
            <option value="startdate">Start date</option>
            <option value="enddate">End date</option>
          </select>
          <select
            value={order}
            onChange={(e) => { setOrder(e.target.value); setPage(1); }}
            className="rounded-xl border border-gray-200 px-3 py-2 text-sm"
          >
            <option value="desc">Desc</option>
            <option value="asc">Asc</option>
          </select>
        </div>
      </div>

      {/* Error state */}
      {isError && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error?.data?.message || "Failed to load notes."}
        </div>
      )}

      {/* Table */}
      <NoteTable notes={notes} loading={loading} />

      {/* Pagination footer */}
      <div className="flex items-center justify-between text-sm text-neutral-600">
        <div>
          {loading ? 'Loading…' : `${from}–${to} of ${total?.toLocaleString?.() ?? total}`}
        </div>

        <div className="flex items-center gap-3">
          {/* Page size */}
          <label className="text-sm text-neutral-500 mr-1">Rows:</label>
          <select
            value={limit}
            onChange={onPageSizeChange}
            className="bg-neutral-900 text-neutral-100 border border-neutral-700 rounded-xl px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
          >
            {PAGE_SIZE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>

          {/* Prev/Next */}
            <div className="flex items-center gap-1">
              <button
                  onClick={onPrev}
                  disabled={!canPrev}
                  aria-label="Previous page"
                  title="Previous"
                  className={`pagination-btn h-9 w-9 rounded-full border border-neutral-700 flex items-center justify-center transition
                  outline-none focus:ring-2 focus:ring-sky-500/40 focus:ring-offset-0
                  ${canPrev ? 'hover:bg-neutral-800 active:scale-[0.98]' : 'opacity-40 cursor-not-allowed'}`}
              >
                  <ChevronLeftIcon className="h-4 w-4 text-neutral-700" />
              </button>

              <button
                  onClick={onNext}
                  disabled={!canNext}
                  aria-label="Next page"
                  title="Next"
                  className={`pagination-btn h-9 w-9 rounded-full border border-neutral-700 flex items-center justify-center transition
                  outline-none focus:ring-2 focus:ring-sky-500/40 focus:ring-offset-0
                  ${canNext ? 'hover:bg-neutral-800 active:scale-[0.98]' : 'opacity-40 cursor-not-allowed'}`}
              >
                  <ChevronRightIcon className="h-4 w-4 text-neutral-700" />
              </button>
            </div>
        </div>
      </div>
    </section>
  );
}
